import { useMemo } from "react";
import { buildActionSignal, formatRelativeDateLabel } from "../../../utils/signals";
import { buildDominantInterventionViewModel } from "../../../intervention/buildDominantInterventionViewModel";
import {
  buildCurrentWeekMovementChart,
  collectCompletionDates,
  countEventsInCurrentWeek,
  countUniqueDaysInCurrentWeek,
  countUniqueDaysWithin,
  dayDiffFromNow,
  getCurrentWeekElapsedDays,
  parseLocalDateValue,
  normalizeCanonicalSummary,
  uniqueDateValues,
} from "../../behavior/utils/canonicalSummary";
import { mapStateToCopy } from "../utils/mapStateToCopy";
import { resolveNextAction } from "../utils/resolveNextAction";
import {
  getViewerActionCounts,
  getViewerActiveDaysLast7,
  getViewerActedToday,
  getViewerUsageStreak,
} from "../../shared/selectors/behaviorSummary";

function formatEndsInLabel(value) {
  if (!value) return "";
  const diff = dayDiffFromNow(value) * -1;
  if (diff === null) return "";
  if (diff <= 0) return "Ends today";
  if (diff === 1) return "Ends in 1 day";
  return `Ends in ${diff} days`;
}

function describeVisibleMovement({ checkins = 0, actions = 0 }) {
  if (checkins > 0 && actions > 0) {
    return `${checkins === 1 ? "Check-in" : `${checkins} check-ins`} and ${actions} completed ${actions === 1 ? "habit" : "habits"}`;
  }
  if (actions > 0) {
    return `${actions} completed ${actions === 1 ? "habit" : "habits"}`;
  }
  if (checkins > 0) {
    return "Check-in only";
  }
  return "No visible activity";
}

function cleanStartText(value) {
  return String(value || "").trim().replace(/^starts\s+/i, "");
}

function getStartSignalLabel(commitment, fallback = "") {
  const signal = commitment?.start_signal || commitment?.startSignal || {};
  const learnedText = String(signal?.learned_rhythm?.display_text || "").trim();
  if (signal?.confidence === "learned") {
    return String(signal?.display_text || learnedText || "").trim();
  }
  const displayText = cleanStartText(signal?.display_text);
  const contextText = cleanStartText(signal?.context_text);
  if (displayText || contextText) return displayText || contextText;
  return cleanStartText(fallback);
}

function buildCurrentWeekChart({ checkinDates = [], actionCompletionDates = [] }) {
  return buildCurrentWeekMovementChart({
    checkinDates,
    actionCompletionDates,
  }).map((item) => ({
    ...item,
    summary: describeVisibleMovement({
      checkins: item.checkins,
      actions: item.actionCount,
    }),
  }));
}

function buildSummaryChart(summaryChart = []) {
  return summaryChart
    .map((item) => {
      const date = parseLocalDateValue(item?.date);
      if (!date) return null;
      const checkins = Number(item?.checkins || 0);
      const actionCount = Number(item?.action_count || item?.actionCount || 0);
      const level = Math.min(checkins + Math.min(actionCount, 2), 3);
      return {
        key: item.date,
        label: date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3),
        fullLabel: date.toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
        }),
        active: Boolean(item?.active) || level > 0,
        level,
        checkins,
        actionCount,
        summary: describeVisibleMovement({ checkins, actions: actionCount }),
      };
    })
    .filter(Boolean);
}

function getLatestDateValue(values = []) {
  return [...values]
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = parseLocalDateValue(left)?.getTime() || 0;
      const rightTime = parseLocalDateValue(right)?.getTime() || 0;
      return rightTime - leftTime;
    })[0] || null;
}

function formatPreferredTime(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return "";
  const suffix = hour < 12 ? "AM" : "PM";
  hour = hour % 12 || 12;
  return `${hour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

const OVERALL_GOALS_ID = "__all_goals__";

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeCompletionType(value = "") {
  const normalized = String(value || "").toLowerCase();
  if (["backup", "small_version", "minimum_version"].includes(normalized)) return "small_version";
  if (normalized === "meal_checkin") return "meal_checkin";
  if (normalized === "full") return "full";
  return "";
}

function getCompletionDisplayForDate(commitment, dateKey) {
  const completions = Array.isArray(commitment?.completions) ? commitment.completions : [];
  const completion = completions.find((entry) => entry?.completed && entry?.date === dateKey) || null;
  const metadata = completion?.metadata && typeof completion.metadata === "object" ? completion.metadata : {};
  const completionType = normalizeCompletionType(
    completion?.completion_type ||
      metadata.completion_type ||
      commitment?.completion_type ||
      commitment?.today_completion_type ||
      commitment?.display_completion_type
  );
  return {
    completionType,
    smallVersionTitle: completion?.small_version_title || commitment?.minimum_version_title || "",
  };
}

export function useTodayData({
  summary,
  todayPayload = null,
  behaviorSummary = null,
  intervention,
  checkin,
  recentCheckins,
  checkinStreak,
  activeCommitments,
  todaysCommitments,
  dueCommitments,
  completionOverrideMap,
  activeSprint,
  goals = [],
  selectedGoalId = null,
  onSelectGoal,
  onChooseAction,
  onCheckIn,
  onSendUpdate,
  onOpenPlan,
  onOpenSprint,
  onReviewSprint,
  onBeginInterventionAction,
  onQueueInterventionCompletion,
  onCompleteInterventionAction,
}) {
  return useMemo(() => {
    const localState = intervention?.state || "active";
    const todayStory = todayPayload?.today_story || null;
    const todayEventFeed = Array.isArray(todayPayload?.today_event_feed)
      ? todayPayload.today_event_feed
      : [];
    const todayPlanByHabitId = new Map(
      (Array.isArray(todayPayload?.today_plan) ? todayPayload.today_plan : [])
        .map((item) => [String(item?.habit_id || item?.commitment_id || ""), item])
        .filter(([id]) => Boolean(id))
    );
    const challengeFocusHabitIds = new Set(
      (Array.isArray(todayPayload?.active_sprint_summary?.focus_habit_ids)
        ? todayPayload.active_sprint_summary.focus_habit_ids
        : Array.isArray(todayPayload?.active_sprint_summary?.challenge_focus?.habit_ids)
          ? todayPayload.active_sprint_summary.challenge_focus.habit_ids
          : []
      ).map((id) => String(id))
    );
    const challengePartnerName =
      todayPayload?.active_sprint_summary?.partner?.display_name ||
      todayPayload?.active_sprint_summary?.partner_username ||
      "";
    const todayKey =
      typeof todayPayload?.date === "string" && todayPayload.date
        ? todayPayload.date
        : getLocalDateKey();
    const dominantIntervention = buildDominantInterventionViewModel(intervention);
    const checkinDates = (recentCheckins || [])
      .map((entry) => entry?.date || entry?.created_at || entry?.createdAt || "")
      .filter(Boolean);

    const actionCompletionDates = collectCompletionDates(
      activeCommitments,
      completionOverrideMap
    );
    const activeGoals = (goals || []).filter((goal) => !goal?.completed_at);
    const goalPool = activeGoals.length ? activeGoals : goals || [];
    const isOverallSelection =
      selectedGoalId === null ||
      selectedGoalId === undefined ||
      String(selectedGoalId) === OVERALL_GOALS_ID;
    const selectedGoal = isOverallSelection
      ? null
      : goalPool.find((goal) => String(goal?.id) === String(selectedGoalId)) || null;
    const selectedGoalCompletionDates = selectedGoal
      ? collectCompletionDates(selectedGoal?.commitments, completionOverrideMap)
      : [];
    const progressCompletionDates = selectedGoal ? selectedGoalCompletionDates : actionCompletionDates;
    const goalOptions = [
      { id: OVERALL_GOALS_ID, label: "All goals" },
      ...goalPool.map((goal) => ({
        id: goal.id,
        label: goal.title || "Goal",
      })),
    ];
    const visibleTodayCommitments = selectedGoal
      ? (todaysCommitments || []).filter((commitment) => String(commitment?.goalId) === String(selectedGoal.id))
      : todaysCommitments || [];

    const overallActiveDates = uniqueDateValues([...checkinDates, ...actionCompletionDates]);
    const localActiveDays7 = countUniqueDaysWithin(overallActiveDates, 7);
    const activeDays4 = countUniqueDaysWithin(overallActiveDates, 4);
    const overallActionsCompletedThisWeekLocal = countEventsInCurrentWeek(actionCompletionDates);
    const progressActiveDates = uniqueDateValues([...checkinDates, ...progressCompletionDates]);
    const progressActiveDaysThisWeek = countUniqueDaysInCurrentWeek(progressActiveDates);
    const actionsCompletedThisWeek = countEventsInCurrentWeek(progressCompletionDates);
    const actionsCompletedToday = (todaysCommitments || []).filter(
      (commitment) => completionOverrideMap[commitment.id] ?? commitment.completed_today
    ).length;
    const hasActionCompletionToday = actionsCompletedToday > 0;
    const hasCheckInToday = Boolean(checkin);
    const canonicalSummary = normalizeCanonicalSummary(summary, {
      acted_today: hasCheckInToday || hasActionCompletionToday,
      active_days_last_7: localActiveDays7,
      actions_completed_today: actionsCompletedToday,
      actions_completed_this_week: overallActionsCompletedThisWeekLocal,
      state: localState,
    });
    const canonicalActionCounts = getViewerActionCounts({
      behaviorSummary,
      summary,
      localFallback: {
        completedToday: actionsCompletedToday,
        completedThisWeek: overallActionsCompletedThisWeekLocal,
      },
      sourceLabel: "today-web",
    });
    const summaryChart = Array.isArray(summary?.recent_activity_chart)
      ? buildSummaryChart(summary.recent_activity_chart)
      : [];
    const summaryWeekChart = Array.isArray(summary?.current_week_activity_chart)
      ? buildSummaryChart(summary.current_week_activity_chart)
      : [];
    const resolvedActiveDays4 =
      selectedGoal || !summaryChart.length
        ? activeDays4
        : summaryChart.slice(-4).filter((item) => item?.active).length;
    const summaryLatestActive = [...summaryChart]
      .reverse()
      .find((item) => item?.active)?.key || null;
    const latestOverallActivity = summaryLatestActive || getLatestDateValue(overallActiveDates);
    const resolvedActionsCompletedToday = Math.max(
      canonicalActionCounts.completedToday,
      actionsCompletedToday
    );
    const resolvedActionsCompletedThisWeek = Math.max(
      canonicalActionCounts.completedThisWeek,
      overallActionsCompletedThisWeekLocal
    );
    const resolvedHasActionCompletionToday = resolvedActionsCompletedToday > 0;
    const state = canonicalSummary.userState || localState;
    const activeDays7 = getViewerActiveDaysLast7({
      behaviorSummary,
      summary,
      localFallback: localActiveDays7,
      sourceLabel: "today-web",
    });
    const usageStreak = getViewerUsageStreak({
      behaviorSummary,
      summary,
      localFallback: checkinStreak || activeDays7,
    });
    const hasShownUpToday =
      getViewerActedToday({
        behaviorSummary,
        summary,
        localFallback: hasCheckInToday || resolvedHasActionCompletionToday,
        sourceLabel: "today-web",
      }) || hasCheckInToday || resolvedHasActionCompletionToday;
    const fallbackCopy = mapStateToCopy({
      state,
      hasCheckInToday,
      hasActionCompletionToday: resolvedHasActionCompletionToday,
      actionsCompleted7: resolvedActionsCompletedThisWeek,
    });
    const hasTrustBud = Boolean(activeSprint);

    const nextActions = resolveNextAction({
      dueCount: dueCommitments.length,
      hasShownUpToday,
      hasActionCompletionToday: resolvedHasActionCompletionToday,
      hasTrustBud,
      onChooseAction,
      onCheckIn,
      onSendUpdate,
      onOpenPlan,
      onOpenSprint,
    });

    function resolveInterventionPrimaryAction(actionKey) {
      if (actionKey === "complete_due_action") {
        return async () => {
          const startedAction = await onBeginInterventionAction?.();
          onQueueInterventionCompletion?.(startedAction || actionKey);
          onChooseAction();
        };
      }
      if (actionKey === "quick_checkin") {
        return async () => {
          const startedAction = await onBeginInterventionAction?.();
          await onCheckIn();
          await onCompleteInterventionAction?.(startedAction || actionKey);
        };
      }
      if (
        actionKey === "send_short_update" ||
        actionKey === "send_restart_update" ||
        actionKey === "send_repair_update"
      ) {
        return async () => {
          await onBeginInterventionAction?.();
          onSendUpdate();
        };
      }
      if (actionKey === "open_renewal_decision") {
        return async () => {
          await onBeginInterventionAction?.();
          (onReviewSprint || onOpenSprint)();
        };
      }
      return nextActions.primary?.onClick || onOpenPlan;
    }

    const heroPrimary = dominantIntervention
      ? {
          label: dominantIntervention.primaryActionLabel,
          onClick: resolveInterventionPrimaryAction(dominantIntervention.primaryActionKey),
        }
      : nextActions.primary;

    let heroSecondary = nextActions.secondary;
    if (dominantIntervention?.primaryActionKey === "open_renewal_decision") {
      heroSecondary = {
        label: "Open Accountability",
        onClick: onReviewSprint || onOpenSprint,
      };
    } else if (
      dominantIntervention?.primaryActionKey === "send_short_update" ||
      dominantIntervention?.primaryActionKey === "send_restart_update" ||
      dominantIntervention?.primaryActionKey === "send_repair_update"
    ) {
      heroSecondary = {
        label: "Open Accountability",
        onClick: onOpenSprint,
      };
    }

    const lastUpdateAt =
      activeSprint?.last_activity_at ||
      activeSprint?.updated_at ||
      activeSprint?.latest_message_at ||
      null;

    const lastUpdateLabel = lastUpdateAt
      ? `Last update ${formatRelativeDateLabel(lastUpdateAt)}`
      : hasTrustBud
        ? "No Accountability update yet"
        : "No active Accountability";

    const signals = [
      {
        key: "days-active",
        label: "active days",
        value:
          hasCheckInToday && !resolvedHasActionCompletionToday
            ? "Today"
            : `${resolvedActiveDays4}/4`,
        tone:
          resolvedHasActionCompletionToday || resolvedActiveDays4 >= 3
            ? "positive"
            : state === "drift"
              ? "warning"
              : "neutral",
      },
      {
        key: "actions-week",
        label: "habits done",
        value: `${resolvedActionsCompletedThisWeek}`,
        tone: resolvedActionsCompletedThisWeek > 0 ? "positive" : "neutral",
      },
    ];

    if (hasTrustBud) {
      signals.push({
        key: "last-update",
        label: "last update",
        value: lastUpdateLabel.replace("Last update ", ""),
        tone: formatRelativeDateLabel(lastUpdateAt) === "today" ? "positive" : "neutral",
        onClick: onOpenSprint,
      });
    } else if (latestOverallActivity) {
      signals.push({
        key: "last-activity",
        label: "last activity",
        value: formatRelativeDateLabel(latestOverallActivity) === "today"
          ? "Today"
          : formatRelativeDateLabel(latestOverallActivity),
        tone: formatRelativeDateLabel(latestOverallActivity) === "today" ? "positive" : "neutral",
      });
    }

    const incompleteActions = [];
    const completedActions = [];
    visibleTodayCommitments.forEach((commitment) => {
      const completedToday = Boolean(completionOverrideMap[commitment.id] ?? commitment.completed_today);
      const completedInWindow = Boolean(commitment.completed_in_window);
      const completionDisplay = getCompletionDisplayForDate(commitment, todayKey);
      const completedStatusLabel =
        completionDisplay.completionType === "small_version"
          ? "Done"
          : completionDisplay.completionType === "meal_checkin"
            ? "Meal check-in done"
            : "Done";
      const planItem = todayPlanByHabitId.get(String(commitment.id)) || null;
      const semanticProfile = commitment.semantic_profile || commitment.semanticProfile || {};
      const habitClarity = commitment.habit_clarity || commitment.habitClarity || {};
      const fallbackStartLabel = [
        habitClarity.cue_label,
        commitment.cue_location_label,
        commitment.cue_anchor,
        commitment.cue_context,
      ].map((item) => String(item || "").trim()).find(Boolean) || "";
      const startLabel = getStartSignalLabel(commitment, fallbackStartLabel);
      const cadence =
        commitment.frequency === "weekly"
          ? "Weekly"
          : commitment.frequency === "monthly"
            ? "Monthly"
            : "Daily";
      const daysRemaining =
        typeof commitment.days_remaining === "number" && commitment.frequency !== "daily"
          ? commitment.days_remaining
          : null;
      const signal = buildActionSignal({
        completionDates: (commitment.completions || [])
          .filter((completion) => completion?.completed && completion?.date)
          .map((completion) => completion.date),
        completedToday,
      });
      const signalText =
        commitment.frequency === "weekly" || commitment.frequency === "monthly"
          ? completedInWindow
            ? `${cadence} habit complete`
            : daysRemaining === 0
              ? `${cadence} habit due now`
              : `${cadence} habit · ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`
          : signal.summary;
      const item = {
        id: commitment.id,
        title: commitment.title,
        backupTitle: commitment.minimum_version_title || "",
        doneCriteria: commitment.done_criteria || "",
        cueLabel: startLabel,
        startLabel,
        intelligenceNote: commitment.intelligence_note || "",
        challengeNote:
          challengeFocusHabitIds.has(String(commitment.id)) && challengePartnerName
            ? `Counts for Accountability with ${challengePartnerName}`
            : "",
        plannedStatus: planItem?.status || null,
        plannedTimeLabel: planItem?.time_label || formatPreferredTime(commitment.preferred_time),
        semanticType: semanticProfile.commitment_type || semanticProfile.type || commitment.commitment_type || "",
        semanticDomain: semanticProfile.domain || commitment.domain || "",
        statusLabel: completedToday || completedInWindow ? completedStatusLabel : "",
        supportText:
          completionDisplay.completionType === "small_version"
            ? completionDisplay.smallVersionTitle
            : completionDisplay.completionType === "meal_checkin"
              ? "That check-in counted for today."
              : "",
        subtitle: [
          commitment.goalTitle ? `For ${commitment.goalTitle}` : "",
          commitment.frequency === "weekly" ? "Weekly" : commitment.frequency === "monthly" ? "Monthly" : "",
        ].filter(Boolean).join(" · "),
        signal: signalText,
        completedToday,
        completedInWindow,
        canResetToday: true,
        recommended: false,
      };
      if (completedToday || completedInWindow) {
        completedActions.push(item);
      } else {
        incompleteActions.push(item);
      }
    });

    const currentWeekDays = getCurrentWeekElapsedDays();
    const consistencyPercent = Math.round(
      (progressActiveDaysThisWeek / Math.max(currentWeekDays, 1)) * 100
    );
    const chartData =
      selectedGoal
        ? buildCurrentWeekChart({
            checkinDates,
            actionCompletionDates: progressCompletionDates,
          })
        : summaryWeekChart.length
          ? summaryWeekChart
          : buildCurrentWeekChart({
              checkinDates,
              actionCompletionDates: progressCompletionDates,
            });
    const progressHelper =
      selectedGoal?.baseline_delta !== undefined && selectedGoal?.baseline_delta !== null
        ? `${selectedGoal.baseline_delta}${selectedGoal.unit ? ` ${selectedGoal.unit}` : ""} so far`
        : selectedGoal
          ? actionsCompletedThisWeek > 0
            ? "Work showed up on this goal this week"
            : "Nothing on this goal yet this week"
          : resolvedActionsCompletedThisWeek > 0
            ? "This week has real activity"
            : "You showed up this week. Now focus on one small habit.";

    const recommendedActionId = incompleteActions[0]?.id ?? null;
    const prioritizedIncompleteActions = incompleteActions
      .map((action) => ({
        ...action,
        recommended: action.id === recommendedActionId,
      }))
      .sort((left, right) => Number(Boolean(right.recommended)) - Number(Boolean(left.recommended)));

    const sprint = activeSprint
      ? {
          title: `Accountability with ${activeSprint?.other_user?.username || "your TrustBud"}`,
          status: "Active",
          endsIn: formatEndsInLabel(activeSprint?.ends_at || activeSprint?.end_date),
          lastUpdate:
            state === "drift"
              ? "Start with one step or send a quick update."
              : state === "reentry"
                ? "Pick this back up with one step or a short update."
                : "Last update: today",
          alertTitle:
            state === "drift"
              ? "Nothing has happened yet today"
              : state === "reentry"
                ? "Start again today"
                : "Last update: today",
          profileImage: activeSprint?.other_user?.profile_image || "",
        }
      : {
          title: "No active Accountability",
          status: "Quiet",
          endsIn: "",
          lastUpdate: "Start one when you want accountability.",
          alertTitle: "Nothing active here yet",
          profileImage: "",
        };

    return {
      state,
      copy: dominantIntervention
        ? {
            chip: dominantIntervention.chip,
            title: dominantIntervention.title,
            body: dominantIntervention.body,
            tone: dominantIntervention.tone,
          }
        : fallbackCopy,
      ring: {
        value: usageStreak,
        total: 7,
        label: "day streak",
        updatedToday: hasCheckInToday,
      },
      hero: {
        primary: heroPrimary,
        secondary: heroSecondary,
        partnerContextLine: dominantIntervention?.partnerContextLine || "",
        supportingLine: dominantIntervention?.supportingLine || "",
        successMessage: dominantIntervention?.successMessage || "",
      },
      signals,
      actions: {
        leftCount: incompleteActions.length,
        incomplete: prioritizedIncompleteActions,
        completed: completedActions,
        empty: incompleteActions.length === 0,
        emptyTitle:
          visibleTodayCommitments.length === 0
            ? "Nothing is due right now"
            : "You cleared the habits in play",
        emptyKind: visibleTodayCommitments.length === 0 ? "none_due" : "cleared",
      },
      story: todayStory
        ? {
            title: todayStory.title || "Today story",
            body: todayStory.body || "",
            events: todayEventFeed.slice(0, 3),
          }
        : null,
      progress: {
        actionsCompleted7: selectedGoal
          ? actionsCompletedThisWeek
          : resolvedActionsCompletedThisWeek,
        consistencyPercent,
        helper: progressHelper,
        chartData,
        goalOptions,
        selectedGoalId: selectedGoal?.id || OVERALL_GOALS_ID,
        onSelectGoal,
      },
      sprint,
      history: chartData,
    };
  }, [
    behaviorSummary,
    summary,
    todayPayload,
    intervention,
    checkin,
    recentCheckins,
    checkinStreak,
    activeCommitments,
    todaysCommitments,
    dueCommitments.length,
    completionOverrideMap,
    activeSprint,
    goals,
    selectedGoalId,
    onSelectGoal,
    onChooseAction,
    onCheckIn,
    onSendUpdate,
    onOpenPlan,
    onOpenSprint,
    onReviewSprint,
    onBeginInterventionAction,
    onQueueInterventionCompletion,
    onCompleteInterventionAction,
  ]);
}
